import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import ResourceMap from "./ResourceMap";
import { PermissionsProvider } from "../contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Play, Clock, Loader2 } from "lucide-react";

export default function DemoView() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const sessionRef = useRef(null);
  const originalTokenRef = useRef(null);
  const originalEventRef = useRef(null);

  // Start demo session
  useEffect(() => {
    const startDemo = async () => {
      try {
        // Gem originale localStorage-værdier
        originalTokenRef.current = localStorage.getItem("token");
        originalEventRef.current = {
          id: localStorage.getItem("selectedEventId"),
          name: localStorage.getItem("selectedEventName"),
          archived: localStorage.getItem("selectedEventArchived"),
          logo: localStorage.getItem("selectedEventLogoUrl"),
        };

        const res = await axios.post(`${API}/demo/start/${linkId}`);
        const data = res.data;
        sessionRef.current = data.session_id;

        // Sæt demo-token og event i localStorage (ResourceMap bruger det)
        localStorage.setItem("token", data.token);
        localStorage.setItem("selectedEventId", data.event.id);
        localStorage.setItem("selectedEventName", data.event.name);
        localStorage.setItem("selectedEventArchived", "false");
        localStorage.removeItem("selectedEventLogoUrl");

        setSessionData(data);
        setTimeLeft(30 * 60); // 30 min i sekunder
      } catch (err) {
        setError(
          err.response?.data?.detail || "Kunne ikke starte demo-session"
        );
      } finally {
        setLoading(false);
      }
    };
    startDemo();

    return () => {
      // Cleanup ved unmount
      cleanup();
    };
  }, [linkId]);

  // Timer
  useEffect(() => {
    if (!sessionData || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          cleanup();
          navigate("/");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionData, navigate]);

  // Cleanup: genindsæt originale localStorage og slet demo-session
  const cleanup = useCallback(async () => {
    const sid = sessionRef.current;
    if (sid) {
      try {
        await axios.post(`${API}/demo/end/${sid}`);
      } catch {}
      sessionRef.current = null;
    }

    // Gendan originale localStorage
    const orig = originalTokenRef.current;
    const origEvent = originalEventRef.current;
    if (orig) {
      localStorage.setItem("token", orig);
    } else {
      localStorage.removeItem("token");
    }
    if (origEvent?.id) {
      localStorage.setItem("selectedEventId", origEvent.id);
      localStorage.setItem("selectedEventName", origEvent.name || "");
      localStorage.setItem(
        "selectedEventArchived",
        origEvent.archived || "false"
      );
      if (origEvent.logo)
        localStorage.setItem("selectedEventLogoUrl", origEvent.logo);
    } else {
      localStorage.removeItem("selectedEventId");
      localStorage.removeItem("selectedEventName");
      localStorage.removeItem("selectedEventArchived");
      localStorage.removeItem("selectedEventLogoUrl");
    }
  }, []);

  // Cleanup ved browser-lukning
  useEffect(() => {
    const handleBeforeUnload = () => {
      const sid = sessionRef.current;
      if (sid) {
        navigator.sendBeacon(`${API}/demo/end/${sid}`);
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleExit = async () => {
    await cleanup();
    navigate("/");
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#5B8C2A] animate-spin mx-auto mb-4" />
          <p className="text-zinc-400 text-sm">
            Klargør demo-miljø...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-8 max-w-md text-center">
          <Play className="h-12 w-12 text-zinc-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">
            Demo ikke tilgængelig
          </h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Button onClick={() => navigate("/")} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" /> Tilbage til forsiden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col" data-testid="demo-view">
      {/* Demo banner */}
      <div className="bg-[#5B8C2A] text-white px-4 py-2 flex items-center justify-between z-[60] shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExit}
            className="text-white/80 hover:text-white hover:bg-white/10 h-8 px-2"
            data-testid="demo-exit-btn"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-medium">
            <span className="hidden sm:inline">
              Du prøver Resovia — alle funktioner er tilgængelige!
            </span>
            <span className="sm:hidden">Resovia Demo</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full text-xs font-mono" data-testid="demo-timer">
            <Clock className="h-3 w-3" />
            {formatTime(timeLeft)}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExit}
            className="text-white/80 hover:text-white hover:bg-white/10 h-8 text-xs hidden sm:flex"
            data-testid="demo-exit-btn-text"
          >
            Afslut demo
          </Button>
        </div>
      </div>

      {/* ResourceMap med fulde rettigheder */}
      <div className="flex-1 overflow-hidden">
        <PermissionsProvider user={sessionData.user}>
          <ResourceMap
            user={sessionData.user}
            onLogout={handleExit}
            selectedEvent={sessionData.event}
          />
        </PermissionsProvider>
      </div>
    </div>
  );
}
